import { Button, DialogContent, InputAdornment, TextField, debounce, Grid2, useTheme } from '@mui/material';
import { Dialog } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Currencies, CURRENCY_SYMBOLS, OrderProduct, ResourceListItem } from '../../../types';
import { useProductsFindMutation } from '../../../services/products';
import { DialogHeader } from '../../../components/DialogHeader';
import { useGetCompanyVendorProfileQuery } from '../../../services/vendorProfile';
import useAuth from '../../../components/Auth';

const SHOULD_CLEAR_FORM_AFTER_CHOOSING_NON_EXISTING_SKU = false;

interface Props {
    open: boolean;
    resource: ResourceListItem | null;
    onSubmit: (payload: Partial<OrderProduct>, isExistingProduct: boolean) => void;
    onClose: () => void;
}

const SalesOrderAddProductDialog = ({ open, resource, onSubmit, onClose }: Props) => {
	const [formData, setFormData] = useState<Partial<OrderProduct>>({});
	const [skuMatch, skuMatchSetState] = useState<OrderProduct | null | false>(null);
	const [productsFindMutation] = useProductsFindMutation(); 
	const theme = useTheme();
	const {userProfile} = useAuth();
	const {data: vendorProfile} = useGetCompanyVendorProfileQuery(undefined, {skip: !userProfile});
	const currencySymbol = CURRENCY_SYMBOLS[vendorProfile?.currency as Currencies];

	// reset form data on each open
	useEffect(() => {
		if (!open) {
			return;
		}
		setFormData(resource ? {
			resourceId: resource.id,
			quantity: resource.quantity,
			uom: resource.uom,
		} : {});
		skuMatchSetState(null);
	}, [resource, open]);

	const handleFormFieldChange = (e: React.ChangeEvent<any>) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		});
	};

	const onAccept = useCallback((sku: string) => {
		productsFindMutation({
			where: {
				sku: sku.trim()
			}
		}).unwrap().then((results) => {
			const [match] = results;
			skuMatchSetState(match ?? false);
		});
	}, []);

	useEffect(() => {
		if (skuMatch) {
			setFormData({
				...formData,
				uom: skuMatch.uom,
				name: skuMatch.name,
				pricePerUnit: skuMatch.pricePerUnit,
				imgUrl: skuMatch.imgUrl,
			});
		}
		if (skuMatch === false && SHOULD_CLEAR_FORM_AFTER_CHOOSING_NON_EXISTING_SKU) {
			setFormData({
				...formData,
				uom: resource ? resource.uom : '',
				name: '',
				pricePerUnit: '',
				imgUrl: '',
			});
		}
	}, [skuMatch]);

	const handleSKUChangeDebounced = React.useMemo(
		() => debounce(onAccept, 500),
		[onAccept],
	);

	const handleSKUChange = (e: React.ChangeEvent<any>) => {
		handleFormFieldChange(e);
		skuMatchSetState(null);
		handleSKUChangeDebounced(e.target.value);
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSubmit(formData, Boolean(skuMatch));
	};

	const isSKUEmpty = useMemo(() => !formData.sku, [formData]);
	const headerTitle = useMemo(() => {
		if (skuMatch) {
			return `Add existing product: ${skuMatch.name}`;
		}
		if (!isSKUEmpty && skuMatch === null) {
			return '...';
		}
		return 'Add new product';
	}, [skuMatch, isSKUEmpty]);

	return <Dialog open={open} fullWidth onClose={onClose}>
		<DialogHeader 
			title={headerTitle} 
			imgUrl={skuMatch ? skuMatch.imgUrl : undefined} 
			imgPlaceholder={true}
			onClose={onClose} 
		/>
		<DialogContent>
			<form onSubmit={handleSubmit}>
				<Grid2 container spacing={2}>
					<Grid2>
						<TextField fullWidth required autoComplete='off' label='SKU' name='sku' value={formData.sku ?? ''} onChange={handleSKUChange} />
					</Grid2>
					<Grid2>
						<TextField sx={{ bgcolor: skuMatch ? theme.palette.action.disabledBackground : undefined}} disabled={Boolean(skuMatch)} autoComplete='off' label='Image URL' name='imgUrl' value={formData.imgUrl ?? ''} onChange={handleFormFieldChange} />
					</Grid2>
					<Grid2 size={12}>
						<TextField fullWidth sx={{bgcolor: skuMatch ? theme.palette.action.disabledBackground : undefined}} disabled={Boolean(skuMatch)} required autoComplete='off' label='Name' name='name' value={formData.name ?? ''} onChange={handleFormFieldChange} />
					</Grid2>
					<Grid2 size={12}>
						<TextField multiline rows={3} fullWidth sx={{bgcolor: skuMatch ? theme.palette.action.disabledBackground : undefined}} disabled={Boolean(skuMatch)} autoComplete='off' label='Description' name='description' value={formData.description ?? ''} onChange={handleFormFieldChange} />
					</Grid2>
					<Grid2 size='grow'>
						<TextField fullWidth required type='number' autoComplete='off' label='Quantity' name='quantity' value={formData.quantity ?? ''} onChange={handleFormFieldChange} />
					</Grid2>
					<Grid2 size='grow'>
						<TextField sx={{bgcolor: skuMatch ? theme.palette.action.disabledBackground : undefined}} disabled={Boolean(skuMatch)} required autoComplete='off' label='Unit of measure' name='uom' value={formData.uom ?? ''} onChange={handleFormFieldChange} />
					</Grid2>
					<Grid2 size='grow'>
						<TextField required type='number' disabled={Boolean(skuMatch) && (skuMatch as OrderProduct)?.isBundle} autoComplete='off' label='Price per unit' name='pricePerUnit' value={formData.pricePerUnit ?? ''} onChange={handleFormFieldChange} InputProps={{
							endAdornment: <InputAdornment position="end">{currencySymbol}</InputAdornment>,
						}}/>
					</Grid2>
					{/* notes  */}
					<Grid2 size={12}>
						<TextField multiline rows={3} fullWidth sx={{bgcolor: skuMatch ? theme.palette.action.disabledBackground : undefined}} disabled={Boolean(skuMatch)} autoComplete='off' label='Notes' name='notes' value={formData.notes ?? ''} onChange={handleFormFieldChange} />
					</Grid2>
					<Grid2 size={12}>
						<Button type='submit' variant='contained'>Add</Button>
					</Grid2>
				</Grid2>
			</form>
		</DialogContent>
	</Dialog>;   
};

export default SalesOrderAddProductDialog;
